import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Activity, Compass, Users, Heart, BookmarkCheck,
  Handshake, MessageSquare, Settings, Landmark, Sparkles,
  Loader2, CheckCircle2, ExternalLink, ShieldCheck, Wallet, PiggyBank, X, Headset, Phone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'];
const CONDITIONS = ['CHF', 'COPD', 'Post-surgical', 'Diabetes', 'Stroke recovery', 'Dementia', 'Mobility / falls', 'Other'];

const TYPE_STYLE = {
  VA: 'bg-blue-100 text-blue-700', Medicaid: 'bg-emerald-100 text-emerald-700',
  Medicare: 'bg-purple-100 text-purple-700', State: 'bg-amber-100 text-amber-700',
  LTC: 'bg-cn-soft text-cn-primary',
};

const Toggle = ({ label, checked, onChange, testId }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
    className={`flex items-center justify-between w-full px-3 py-2.5 rounded-xl border text-sm transition-all ${checked ? 'border-cn-primary bg-cn-soft text-cn-text' : 'border-cn-border text-cn-text-muted hover:border-cn-primary/40'}`}>
    <span>{label}</span>
    <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${checked ? 'bg-cn-primary justify-end' : 'bg-cn-border justify-start'}`}>
      <span className="w-4 h-4 rounded-full bg-white" />
    </span>
  </button>
);

export default function BenefitsNavigator() {
  const { getAuthHeader, token } = useAuth();
  const [form, setForm] = useState({
    state: '', zip_code: '', age: '', is_veteran: false, spouse_of_veteran: false,
    monthly_income: '', household_size: '1', has_ltc_policy: false, on_medicaid: false,
    homebound: false, needs_help_adls: false, condition_focus: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState([]);
  const [appliedTotal, setAppliedTotal] = useState(0);
  const [applyingId, setApplyingId] = useState(null);
  const [zipLooking, setZipLooking] = useState(false);
  const [showCounselor, setShowCounselor] = useState(false);
  const [counselorForm, setCounselorForm] = useState({ name: '', contact_phone: '', preferred_time: '', note: '' });
  const [counselorSubmitting, setCounselorSubmitting] = useState(false);
  const [counselorSent, setCounselorSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Optional ZIP -> state auto-fill (offline lookup on the backend)
  const handleZip = (val) => {
    setForm((f) => ({ ...f, zip_code: val }));
    const digits = (val || '').replace(/\D/g, '');
    if (digits.length === 5) {
      setZipLooking(true);
      axios.get(`${API}/geo/zip/${digits}`)
        .then(({ data }) => {
          if (data.state) {
            setForm((f) => ({ ...f, state: data.state }));
            toast.success(`State auto-filled from ZIP: ${data.state}`);
          }
        })
        .catch(() => { /* unknown ZIP — leave state as-is */ })
        .finally(() => setZipLooking(false));
    }
  };

  const submitCounselor = async () => {
    if (!counselorForm.name.trim()) { toast.error('Please enter your name'); return; }
    if (!counselorForm.contact_phone.trim()) { toast.error('A phone number is required'); return; }
    setCounselorSubmitting(true);
    try {
      await axios.post(`${API}/benefits/counselor-request`, {
        ...counselorForm, state: form.state || null,
      }, getAuthHeader());
      setCounselorSent(true);
      toast.success('A benefits counselor will reach out');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send request'); }
    finally { setCounselorSubmitting(false); }
  };

  const loadApplied = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/benefits/applications`, getAuthHeader());
      setApplied(data.applications);
      setAppliedTotal(data.estimated_monthly_total);
    } catch (e) { /* noop */ }
  }, [getAuthHeader]);

  useEffect(() => { if (token) loadApplied(); }, [token, loadApplied]);

  const findBenefits = async () => {
    if (!form.state) { toast.error('Select your state'); return; }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        household_size: Number(form.household_size) || 1,
      };
      const { data } = await axios.post(`${API}/benefits/eligibility`, payload, getAuthHeader());
      setResult(data);
      if (data.count === 0) toast.info('No matched programs — try adjusting the profile.');
    } catch (e) { toast.error('Could not check eligibility'); }
    finally { setLoading(false); }
  };

  const apply = async (p) => {
    setApplyingId(p.id);
    try {
      await axios.post(`${API}/benefits/apply`, {
        program_id: p.id, program_name: p.name, program_type: p.type, est_monthly_value: p.est_monthly_value,
      }, getAuthHeader());
      toast.success(`${p.name} added to your care funding`);
      await loadApplied();
    } catch (e) { toast.error('Could not apply'); }
    finally { setApplyingId(null); }
  };

  const remove = async (id) => {
    setApplied((prev) => prev.filter((a) => a.id !== id));
    try { await axios.delete(`${API}/benefits/applications/${id}`, getAuthHeader()); await loadApplied(); }
    catch (e) { loadApplied(); }
  };

  const appliedIds = new Set(applied.filter((a) => a.status !== 'removed').map((a) => a.program_id));

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator' },
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians' },
    { icon: Heart, label: 'Saved Clinicians', href: '/patient/saved-clinicians' },
    { icon: Landmark, label: 'Benefits & Funding', active: true, highlight: true },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Benefits & Funding Navigator"
      subtitle="Find grants, VA, Medicaid & LTC benefits that help pay for care — and apply them to your visits."
      testId="benefits-navigator-page"
    >
      {/* Care funding summary */}
      {appliedTotal > 0 && (
        <div className="cn-card rounded-2xl p-5 mb-5 bg-gradient-to-br from-cn-soft to-white border border-cn-primary/20" data-testid="funding-summary">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center"><PiggyBank className="w-6 h-6" /></div>
              <div>
                <p className="text-2xl font-display font-extrabold text-cn-text" data-testid="funding-total">${appliedTotal.toLocaleString()}<span className="text-sm font-normal text-cn-text-muted">/mo est.</span></p>
                <p className="text-xs text-cn-text-muted">in benefits earmarked toward your care · applied as a credit at checkout</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {applied.filter((a) => a.status !== 'removed').map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-cn-border rounded-full pl-3 pr-1.5 py-1" data-testid={`applied-${a.program_id}`}>
                  {a.program_name}
                  <button onClick={() => remove(a.id)} className="w-5 h-5 rounded-full hover:bg-cn-soft flex items-center justify-center" data-testid={`remove-applied-${a.id}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Eligibility form */}
        <div className="lg:col-span-2">
          <div className="cn-card rounded-2xl p-5 space-y-4" data-testid="benefits-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">State *</Label>
                <Select value={form.state} onValueChange={set('state')}>
                  <SelectTrigger data-testid="benefits-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">ZIP code</Label>
                <div className="relative">
                  <Input value={form.zip_code} onChange={(e) => handleZip(e.target.value)} placeholder="78701" data-testid="benefits-zip" />
                  {zipLooking && <Loader2 className="w-4 h-4 animate-spin text-cn-primary absolute right-2 top-1/2 -translate-y-1/2" data-testid="benefits-zip-looking" />}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Age</Label>
                <Input type="number" value={form.age} onChange={(e) => set('age')(e.target.value)} placeholder="72" data-testid="benefits-age" />
              </div>
              <div>
                <Label className="text-xs">Household size</Label>
                <Input type="number" value={form.household_size} onChange={(e) => set('household_size')(e.target.value)} placeholder="1" data-testid="benefits-household" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Monthly household income</Label>
              <Input type="number" value={form.monthly_income} onChange={(e) => set('monthly_income')(e.target.value)} placeholder="2000" data-testid="benefits-income" />
            </div>
            <div>
              <Label className="text-xs">Primary condition</Label>
              <Select value={form.condition_focus} onValueChange={set('condition_focus')}>
                <SelectTrigger data-testid="benefits-condition"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 pt-1">
              <Toggle label="Veteran or surviving spouse" checked={form.is_veteran} onChange={set('is_veteran')} testId="benefits-veteran" />
              <Toggle label="Needs help with daily activities" checked={form.needs_help_adls} onChange={set('needs_help_adls')} testId="benefits-adls" />
              <Toggle label="Largely homebound" checked={form.homebound} onChange={set('homebound')} testId="benefits-homebound" />
              <Toggle label="Has long-term care insurance" checked={form.has_ltc_policy} onChange={set('has_ltc_policy')} testId="benefits-ltc" />
              <Toggle label="Currently on Medicaid" checked={form.on_medicaid} onChange={set('on_medicaid')} testId="benefits-medicaid" />
            </div>
            <button onClick={findBenefits} disabled={loading} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="benefits-find-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Find my benefits
            </button>
            <p className="text-[11px] text-cn-text-muted">Estimates are illustrative and not a determination of eligibility. Final amounts are set by each program.</p>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3">
          {!result && !loading && (
            <div className="cn-card rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center" data-testid="benefits-empty">
              <div className="w-16 h-16 rounded-3xl bg-cn-soft text-cn-primary flex items-center justify-center mb-3"><Wallet className="w-8 h-8" /></div>
              <p className="text-cn-text font-medium">Most families leave money on the table.</p>
              <p className="text-sm text-cn-text-muted mt-1 max-w-sm">Tell us about your situation and we'll surface the grants, VA, Medicaid and LTC benefits that can help pay for care.</p>
            </div>
          )}
          {loading && <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>}
          {result && (
            <div data-testid="benefits-results">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-cn-text" data-testid="benefits-result-meta">
                  {result.count} program{result.count === 1 ? '' : 's'} you may qualify for
                  {result.estimated_monthly_total > 0 && <span className="text-cn-primary"> · up to ${result.estimated_monthly_total.toLocaleString()}/mo</span>}
                </p>
              </div>
              {result.count === 0 ? (
                <div className="cn-card rounded-2xl p-8 text-center text-cn-text-muted" data-testid="benefits-no-results">No matched programs for this profile. Try adjusting age, income, or veteran status.</div>
              ) : (
                <div className="space-y-3">
                  {result.programs.map((p) => {
                    const isApplied = appliedIds.has(p.id);
                    return (
                      <div key={p.id} className="cn-card rounded-2xl p-5" data-testid={`program-${p.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${TYPE_STYLE[p.type] || 'bg-cn-soft text-cn-text'}`}>{p.type}</span>
                              <h3 className="font-display font-semibold text-cn-text">{p.name}</h3>
                            </div>
                            <p className="text-sm text-cn-text-muted mt-1.5">{p.coverage}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {p.est_monthly_value > 0 ? (
                              <p className="font-display font-bold text-lg text-cn-text">${p.est_monthly_value.toLocaleString()}<span className="text-xs text-cn-text-muted font-normal">/mo</span></p>
                            ) : p.est_monthly_value === 0 ? (
                              <p className="text-xs font-bold text-emerald-600">$0 to you</p>
                            ) : (
                              <p className="text-xs text-cn-text-muted">Per policy</p>
                            )}
                          </div>
                        </div>
                        {p.next_steps?.length > 0 && (
                          <ul className="mt-3 space-y-1">
                            {p.next_steps.map((s, i) => (
                              <li key={i} className="text-xs text-cn-text flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> {s}</li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => apply(p)}
                            disabled={applyingId === p.id || isApplied}
                            className={`cn-btn ${isApplied ? 'cn-btn-ghost text-emerald-600' : 'cn-btn-primary'} !py-2 text-sm`}
                            data-testid={`apply-${p.id}`}
                          >
                            {applyingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isApplied ? <CheckCircle2 className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                            {isApplied ? 'Applied to care' : 'Apply toward my care'}
                          </button>
                          <a href={p.apply_url} target="_blank" rel="noreferrer" className="cn-btn cn-btn-ghost !py-2 text-sm" data-testid={`learn-${p.id}`}>
                            How to apply <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Benefits-counselor handoff */}
      <div className="cn-card rounded-2xl p-5 mt-6 border border-cn-primary/15 bg-gradient-to-br from-white to-cn-soft/40" data-testid="counselor-card">
        {counselorSent ? (
          <div className="flex items-center gap-3 py-2" data-testid="counselor-success">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 flex-shrink-0" />
            <div>
              <p className="font-display font-semibold text-cn-text">Request received</p>
              <p className="text-sm text-cn-text-muted">A benefits counselor will call you to help complete and file the paperwork.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center flex-shrink-0"><Headset className="w-6 h-6" /></div>
                <div>
                  <p className="font-display font-semibold text-cn-text">Need help applying?</p>
                  <p className="text-sm text-cn-text-muted">Applications can be confusing. A benefits counselor can walk you through the paperwork — free.</p>
                </div>
              </div>
              {!showCounselor && (
                <button onClick={() => setShowCounselor(true)} className="cn-btn cn-btn-primary !py-2 text-sm" data-testid="counselor-open-btn">
                  <Phone className="w-4 h-4" /> Talk to a counselor
                </button>
              )}
            </div>
            {showCounselor && (
              <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="counselor-form">
                <div>
                  <Label className="text-xs">Your name *</Label>
                  <Input value={counselorForm.name} onChange={(e) => setCounselorForm((f) => ({ ...f, name: e.target.value }))} data-testid="counselor-name" />
                </div>
                <div>
                  <Label className="text-xs">Phone *</Label>
                  <Input value={counselorForm.contact_phone} onChange={(e) => setCounselorForm((f) => ({ ...f, contact_phone: e.target.value }))} placeholder="(555) 555-5555" data-testid="counselor-phone" />
                </div>
                <div>
                  <Label className="text-xs">Best time to call</Label>
                  <Input value={counselorForm.preferred_time} onChange={(e) => setCounselorForm((f) => ({ ...f, preferred_time: e.target.value }))} placeholder="Weekday mornings" data-testid="counselor-time" />
                </div>
                <div>
                  <Label className="text-xs">Anything we should know?</Label>
                  <Textarea value={counselorForm.note} onChange={(e) => setCounselorForm((f) => ({ ...f, note: e.target.value }))} rows={1} data-testid="counselor-note" />
                </div>
                <div className="sm:col-span-2">
                  <button onClick={submitCounselor} disabled={counselorSubmitting} className="cn-btn cn-btn-primary !py-2.5 text-sm" data-testid="counselor-submit">
                    {counselorSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Headset className="w-4 h-4" />} Request a call
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
